import { parseLocaleNumber } from "@react/helpers/helpers"

export const getWeekKey = date => {
  if (!date) return null
  const day    = date.getDay()
  const diff   = date.getDate() - day + (day === 0 ? -6 : 1) 
  const monday = new Date(date.getTime())
  monday.setDate(diff)
  
  const year       = monday.getFullYear()
  const month      = String(monday.getMonth() + 1).padStart(2, '0')
  const dayOfMonth = String(monday.getDate()).padStart(2, '0')
  
  return `${year}-${month}-${dayOfMonth}`
}

export const getMonthKey = date => {
  if (!date) return null
  const year  = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${year}-${month}`
}

export const getTimeSlot = timeStr => {
  if (!timeStr) return null
  const parts = timeStr.split(' - ')
  if (parts.length !== 2) return null
  const timePart = parts[1] 
  const hour     = parseInt(timePart.split(':')[0], 10)
  if (isNaN(hour)) return null
  return `${hour.toString().padStart(2, '0')}:00`
}

export const parseDateFromString = dateStr => {
  try {
    const [datePart, timePart] = dateStr.split(' - ');
    const [day, month, year]   = datePart.split('/').map(Number);
    
    if (timePart) {
      const [hour, minute] = timePart.split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute);
    } else {
      return new Date(year, month - 1, day);
    }
  } catch (e) {
    return null;
  }
}

export const transformers = {
  
  countByField: (rows, params) => {
    const { field } = params
    const counts = {}
    rows.forEach(row => {
      if (!row[field]) return
      const value   = row[field]
      counts[value] = (counts[value] || 0) + 1
    })
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
  },

  countByFieldFiltered: (rows, params) => {
    const { field, filter_field, filter_values } = params
    const counts = {}
    rows.forEach(row => {
      if (filter_field && filter_values) {
        const filterValue = row[filter_field]
        if (!filter_values.includes(filterValue)) return
      }
      
      if (!row[field]) return
      const value   = row[field]
      counts[value] = (counts[value] || 0) + 1
    })
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
  },

  countByFieldMultiFiltered: (rows, params) => {
    const { field, filter_field, filter_values, status_field, status_values, date_field, date_condition } = params
    const counts = {}
    const now = new Date()
    
    rows.forEach(row => {
      if (filter_field && filter_values) {
        const filterValue = row[filter_field]
        if (!filter_values.includes(filterValue)) return
      }
      
      if (status_field && status_values) {
        const statusValue = row[status_field]
        if (!status_values.includes(statusValue)) return
      }
      
      if (date_field && date_condition === 'past') {
        const date = parseDateFromString(row[date_field])
        if (!date || date >= now) return
      }
      
      if (!row[field]) return
      const value   = row[field]
      counts[value] = (counts[value] || 0) + 1
    })
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
  },

  countByFieldWithGrouping: (rows, params) => {
    const { field, grouping = {} } = params
    const counts = {}
    
    rows.forEach(row => {
      if (!row[field]) return
      const value = row[field]
      
      let grouped = false
      for (const [groupLabel, groupValues] of Object.entries(grouping)) {
        if (groupValues.includes(value)) {
          counts[groupLabel] = (counts[groupLabel] || 0) + 1
          grouped = true
          break
        }
      }
      
      if (!grouped) {
        counts[value] = (counts[value] || 0) + 1
      }
    })
    
    return Object.entries(counts)
      .sort(([,a], [,b]) => b - a)
  },

  topByCount: (rows, params) => {
    const { field, limit = 10 } = params
    return transformers.countByField(rows, { field })
      .slice(0, limit)
  },

  sumByField: (rows, params) => {
    const { field, sum_field, limit } = params
    const sums = {}
    
    rows.forEach(row => {
      const key   = row[field]
      const value = parseLocaleNumber(row[sum_field])
      if (!key || isNaN(value)) return
      sums[key] = (sums[key] || 0) + value
    })
    
    let result = Object.entries(sums)
      .sort(([, a], [, b]) => b - a)
    
    if (limit) result = result.slice(0, limit)
    return result
  },

  sumByFieldFiltered: (rows, params) => {
    const { field, sum_field, filter_field, filter_values, limit } = params
    const sums = {}
    
    rows.forEach(row => {
      if (filter_field && filter_values) {
        const filterValue = row[filter_field]
        if (!filter_values.includes(filterValue)) return
      }
      
      const key   = row[field]
      const value = parseLocaleNumber(row[sum_field])
      if (!key || isNaN(value)) return
      sums[key] = (sums[key] || 0) + value
    })
    
    let result = Object.entries(sums)
      .sort(([, a], [, b]) => b - a)
    
    if (limit) result = result.slice(0, limit)
    return result
  },

  avgByField: (rows, params) => {
    const { group_by, value_field, limit } = params
    const groups = {}
    
    rows.forEach(row => {
      const key   = row[group_by] || 'Non renseigné'
      const value = parseLocaleNumber(row[value_field])
      if (isNaN(value)) return
      
      if (!groups[key]) groups[key] = []
      groups[key].push(value)
    })
    
    let result = Object.entries(groups)
      .map(([key, values]) => [
        key,
        Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 100) / 100
      ])
      .sort(([, a], [, b]) => b - a)
    
    if (limit) result = result.slice(0, limit)
    return result
  },

  weeklyData: (rows, params) => {
    const { date_field, aggregate = 'count', value_field } = params
    const weeks = {}
    
    rows.forEach(row => {
      const date = parseDateFromString(row[date_field])
      if (!date) return
      
      const weekKey = getWeekKey(date)
      if (!weeks[weekKey]) {
        weeks[weekKey] = { count: 0, values: [], durations: [] }
      }
      
      weeks[weekKey].count++
      
      if (aggregate === 'sum' && value_field) {
        const value = parseLocaleNumber(row[value_field])
        if (!isNaN(value)) {
          weeks[weekKey].values.push(value)
        }
      } else if (aggregate === 'duration' && value_field) {
        const duration = parseInt(row[value_field])
        if (duration > 0) {
          weeks[weekKey].durations.push(duration)
        }
      }
    })
    
    return Object.entries(weeks)
      .map(([week, data]) => {
        let value
        switch (aggregate) {
          case 'sum':
            value = Math.round(data.values.reduce((a, b) => a + b, 0))
            break
          case 'average':
            value = data.values.length > 0 ? Math.round(data.values.reduce((a, b) => a + b, 0) / data.values.length) : 0
            break
          case 'duration':
            value = data.durations.length > 0 ? Math.round(data.durations.reduce((a, b) => a + b, 0) / data.durations.length) : 0
            break
          default:
            value = Math.round(data.count)
        }
        return [week, value]
      })
      .sort(([a], [b]) => a.localeCompare(b))
  },

  monthlyData: (rows, params) => {
    const { date_field, aggregate = 'count', value_field } = params
    const months = {}
    
    rows.forEach(row => {
      const date = parseDateFromString(row[date_field])
      if (!date) return
      
      const monthKey = getMonthKey(date)
      if (!months[monthKey]) {
        months[monthKey] = { count: 0, values: [] }
      }
      
      months[monthKey].count++
      
      if ((aggregate === 'sum' || aggregate === 'average') && value_field) {
        const value = parseLocaleNumber(row[value_field])
        if (!isNaN(value)) {
          months[monthKey].values.push(value)
        }
      }
    })
    
    return Object.entries(months)
      .map(([month, data]) => {
        let value
        switch (aggregate) {
          case 'sum':
            value = Math.round(data.values.reduce((a, b) => a + b, 0))
            break
          case 'average':
            value = data.values.length > 0 
              ? Math.round(data.values.reduce((a, b) => a + b, 0) / data.values.length) 
              : 0
            break
          default:
            value = data.count
        }
        return [month, value]
      })
      .sort(([a], [b]) => a.localeCompare(b))
  },

  binaryFieldAnalysis: (rows, params) => {
    const { field, true_label = 'Oui', false_label = 'Non', check_present = true, match_value } = params
    let hasValue = 0
    let doesntHaveValue = 0
    
    rows.forEach(row => {
      const value = row[field] || ''
      let hasContent
      
      if (match_value !== undefined) {
        hasContent = value === match_value
      } else {
        hasContent = check_present ? value.toString().trim() : Boolean(value)
      }
      
      if (hasContent) {
        hasValue++
      } else {
        doesntHaveValue++
      }
    })
    
    return [
      [true_label, hasValue],
      [false_label, doesntHaveValue]
    ]
  },

  avgDelay: (rows, params) => {
    const { group_by, planned_field, actual_field, unit = 'days' } = params
    const delays = {}
    
    rows.forEach(row => {
      const category  = row[group_by] || 'Non renseigné'
      const startDate = parseDateFromString(row[planned_field])
      const endDate   = parseDateFromString(row[actual_field])
      
      if (startDate && endDate) {
        let delay
        switch (unit) {
          case 'hours':
            delay = (endDate - startDate) / (1000 * 60 * 60)
            break
          case 'minutes':
            delay = (endDate - startDate) / (1000 * 60)
            break
          default:
            delay = (endDate - startDate) / (1000 * 60 * 60 * 24)
        }
        
        if (delay > 0) {
          if (!delays[category]) delays[category] = []
          delays[category].push(delay)
        }
      }
    })
    
    return Object.entries(delays)
      .map(([category, delayList]) => [
        category,
        delayList.reduce((a, b) => a + b, 0) / delayList.length
      ])
      .sort(([,a], [,b]) => b - a)
  },

  weeklyTotalDelay: (rows, params) => {
    const { planned_field, actual_field, unit = 'hours' } = params
    const weeks = {}
    
    rows.forEach(row => {
      if (!row[planned_field] || !row[actual_field]) return
      const plannedDate = parseDateFromString(row[planned_field])
      const actualDate = parseDateFromString(row[actual_field])

      if (plannedDate && actualDate) {
        const weekKey = getWeekKey(plannedDate)
        
        let delay
        switch (unit) {
          case 'hours':
            delay = (actualDate - plannedDate) / (1000 * 60 * 60)
            break
          case 'minutes':
            delay = (actualDate - plannedDate) / (1000 * 60)
            break
          default:
            delay = (actualDate - plannedDate) / (1000 * 60 * 60 * 24)
        }
                
        if (delay > 0) {
          weeks[weekKey] = (weeks[weekKey] || 0) + delay
        }
      }
    })
    
    return Object.entries(weeks)
      .map(([week, totalDelay]) => [
        week,
        Math.round(totalDelay * 100) / 100
      ])
      .sort(([a], [b]) => a.localeCompare(b))
  },
  
  durationComparison: (rows, params) => {
    const { group_by, actual_field, predicted_field } = params
    const groups = {}
    rows.forEach(row => {
      const category        = row[group_by] || 'Non renseigné'
      const actualDuration  = parseInt(row[actual_field])
      const plannedDuration = parseInt(row[predicted_field]) || 0
      
      if (actualDuration > 0 && plannedDuration > 0) {
        if (!groups[category]) {
          groups[category] = { actual: [], planned: [] }
        }
        groups[category].actual.push(actualDuration)
        groups[category].planned.push(plannedDuration)
      }
    })
    return Object.entries(groups).map(([category, data]) => ({
      [group_by]: category,
      [I18n.t('export.widgets.helpers.average_planned_duration')]: Math.round(data.actual.reduce((a, b) => a + b, 0) / data.actual.length),
      [I18n.t('export.widgets.helpers.average_duration')]:         Math.round(data.planned.reduce((a, b) => a + b, 0) / data.planned.length)
    }))
  },

  overtimeRate: (rows, params) => {
    const { group_by, actual_field, predicted_field } = params
    const rates = {}
    rows.forEach(row => {
      const category = row[group_by] || 'Non renseigné'
      const actualDuration = parseInt(row[actual_field])
      const plannedDuration = parseInt(row[predicted_field]) || 0
      
      if (actualDuration > 0 && plannedDuration > 0) {
        const overtime = ((actualDuration - plannedDuration) / plannedDuration) * 100
        
        if (!rates[category]) rates[category] = []
        rates[category].push(overtime)
      }
    })
    
    return Object.entries(rates)
      .map(([category, overtimes]) => {
        const totalOvertime = overtimes.reduce((a, b) => a + b, 0)
        
        if (totalOvertime < 0) return [category, 0]
        
        const averageOvertime = Math.round((totalOvertime / overtimes.length) * 100) / 100
        return [category, Math.max(0, averageOvertime)]
      })
      .sort(([, a], [, b]) => b - a)
  },

  timeSlotData: (rows, params) => {
    const { date_field } = params
    const slots = {}
    rows.forEach(row => {
      const time = row[date_field]
      const slot = getTimeSlot(time)
      if (slot) {
        slots[slot] = (slots[slot] || 0) + 1
      }
    })
    
    return Object.entries(slots)
      .sort(([a], [b]) => a.localeCompare(b))
  },

  sumDurationByField: (rows, params) => {
    const { field, duration_field, limit } = params
    return transformers.sumByField(rows, { 
      field, 
      sum_field: duration_field, 
      limit 
    })
  },

  conversionRate: (rows, params) => {
    const { group_by, status_field, signed_statuses, amount_field, mode = 'count' } = params
    const groups = {}
    
    rows.forEach(row => {
      const key = row[group_by] || 'Non renseigné'
      if (!groups[key]) {
        groups[key] = { total: 0, signed: 0, totalAmount: 0, signedAmount: 0 }
      }
      
      const isSigned = signed_statuses.includes(row[status_field])
      
      if (mode === 'count') {
        groups[key].total++
        if (isSigned) groups[key].signed++
      } else if (mode === 'amount' && amount_field) {
        const amount = parseLocaleNumber(row[amount_field])
        if (!isNaN(amount)) {
          groups[key].totalAmount += amount
          if (isSigned) groups[key].signedAmount += amount
        }
      }
    })
    
    return Object.entries(groups)
      .map(([key, data]) => {
        let rate
        if (mode === 'count') {
          rate = data.total > 0 ? (data.signed / data.total) * 100 : 0
        } else {
          rate = data.totalAmount > 0 ? (data.signedAmount / data.totalAmount) * 100 : 0
        }
        return [key, Math.round(rate * 100) / 100]
      })
      .sort(([, a], [, b]) => b - a)
  },

  bucketDistribution: (rows, params) => {
    const { field, buckets } = params
    const counts = {}
    
    buckets.forEach(bucket => {
      counts[bucket.label] = 0
    })
    
    rows.forEach(row => {
      const value = parseLocaleNumber(row[field])
      if (isNaN(value)) return
      
      for (const bucket of buckets) {
        const matchesMin = bucket.min === undefined || value >= bucket.min
        const matchesMax = bucket.max === undefined || value < bucket.max
        
        if (matchesMin && matchesMax) {
          counts[bucket.label]++
          break
        }
      }
    })
    
    return Object.entries(counts)
  },

  // One bar per workorder: whole days between its creation date and today.
  // Label combines two CSV columns (e.g. workorder number + site code),
  // shown on both the X-axis and the tooltip.
  // No built-in filtering — the exported selection defines which rows are shown.
  daysSinceCreation: (rows, params) => {
    const { date_field, label_field, sublabel_field, limit } = params
    const now = new Date()

    let result = rows
      .map(row => {
        const created = parseDateFromString(row[date_field])
        if (!created) return null
        const days  = Math.floor((now - created) / (1000 * 60 * 60 * 24))
        const label = [row[label_field], row[sublabel_field]].filter(Boolean).join(' - ')
        return [label, days]
      })
      .filter(Boolean)
      .sort(([, a], [, b]) => b - a)

    if (limit) result = result.slice(0, limit)
    return result
  },

  monthlyConversionRate: (rows, params) => {
    const { date_field, status_field, signed_statuses } = params
    const months = {}
    
    rows.forEach(row => {
      const date = parseDateFromString(row[date_field])
      if (!date) return
      
      const monthKey = getMonthKey(date)
      if (!months[monthKey]) {
        months[monthKey] = { total: 0, signed: 0 }
      }
      
      months[monthKey].total++
      if (signed_statuses.includes(row[status_field])) {
        months[monthKey].signed++
      }
    })
    
    return Object.entries(months)
      .map(([month, data]) => {
        const rate = data.total > 0 ? (data.signed / data.total) * 100 : 0
        return [month, Math.round(rate * 100) / 100]
      })
      .sort(([a], [b]) => a.localeCompare(b))
  }
}

export const buildWidgetFromTemplate = (template, currency) => {
  const transformer = transformers[template.transformer_name]
  
  if (!transformer) {
    console.error(`Transformer not found: ${template.transformer_name}`)
    return null
  }
  return {
    identifier:   template.identifier,
    title:        template.title,
    chart_type:   template.chart_type,
    transformer:  (rows) => transformer(rows, template.transformer_params),
    chart_config: template.chart_config || {},
    value_format: template.value_format,
    currency:     currency
  }
}

export const buildWidgetsFromTemplates = (templates, currency = 'EUR') => {
  return templates
    .map(template => buildWidgetFromTemplate(template, currency))
    .filter(Boolean) 
}

export const truncateLabel = (label, maxLength = 18) => {
  if (!label) return ''
  const str = String(label)
  return str.length > maxLength ? str.substring(0, maxLength - 3) + '...' : str
}